'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import * as Accordion from '@radix-ui/react-accordion'
import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FAQItem {
  id: number
  question: string
  answer: string
}

interface FaqAccordionProps {
  data: FAQItem[]
  className?: string
}

export function FaqAccordion({ data, className }: FaqAccordionProps) {
  const [openItem, setOpenItem] = useState<string | undefined>(
    data.length > 0 ? data[0].id.toString() : undefined,
  )

  return (
    <div
      className={cn('w-full', className)}
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <Accordion.Root
        type="single"
        collapsible
        value={openItem}
        onValueChange={setOpenItem}
        className="space-y-4"
      >
        {data.map((item) => (
          <Accordion.Item
            value={item.id.toString()}
            key={item.id}
            className="border border-white/10 rounded-2xl bg-white/5 backdrop-blur-lg transition-all duration-300"
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
          >
            <Accordion.Header>
              <Accordion.Trigger className="flex w-full items-center justify-between gap-x-4 p-6 text-left text-lg font-medium text-white transition-colors hover:text-gray-200">
                <span itemProp="name">{item.question}</span>
                <span className="flex-shrink-0">
                  {openItem === item.id.toString() ? (
                    <Minus className="h-5 w-5" />
                  ) : (
                    <Plus className="h-5 w-5" />
                  )}
                </span>
              </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content asChild forceMount>
              <motion.div
                initial="collapsed"
                animate={openItem === item.id.toString() ? 'open' : 'collapsed'}
                variants={{
                  open: { opacity: 1, height: 'auto' },
                  collapsed: { opacity: 0, height: 0 },
                }}
                transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                className="overflow-hidden"
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                <div className="px-6 pb-6 text-gray-300" itemProp="text">
                  {item.answer}
                </div>
              </motion.div>
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </Accordion.Root>
    </div>
  )
}
